import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FixedLines from '../components/common/FixedLines';
import UiIcon from '../components/common/UiIcon';
import { SITE_CONFIG } from '../config/site';
import { trackEvent } from '../lib/analytics';

const heroDescriptionLines = [
  '좋은 말만 얹지 않습니다.',
  '질문 뒤에 남아 있는 망설임부터 봅니다.',
  '어디서 꼬였고, 지금 무엇을 붙잡아야 하는지',
  '카드의 흐름을 따라 천천히 풀어드릴게요.',
];

const EXCLUDED_HERO_CARD_INDEXES = new Set([0, 32, 46, 58]);

function pickRandomHeroCardIndexes(): [number, number] {
  const maxCards = 78;
  const pickCard = () => {
    let index = Math.floor(Math.random() * maxCards);

    while (EXCLUDED_HERO_CARD_INDEXES.has(index)) {
      index = Math.floor(Math.random() * maxCards);
    }

    return index;
  };

  const first = pickCard();
  let second = pickCard();

  while (second === first) {
    second = pickCard();
  }

  return [first, second];
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [heroCardIndexes] = useState<[number, number]>(() => pickRandomHeroCardIndexes());

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#140f0d] font-display text-white selection:bg-primary selection:text-white">
      <style>{`
      `}</style>

      <div className="pointer-events-none fixed inset-0 z-0 bg-grain opacity-[0.07]" />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(180deg,#140f0d_0%,#17110e_45%,#120d0b_100%)]" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="px-6 pb-6 pt-7 sm:px-8 lg:px-12">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between">
            <Link
              to="/"
              aria-label="메인 페이지로 이동"
              className="flex flex-col transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/60 focus:ring-offset-2 focus:ring-offset-[#140f0d]"
            >
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#ebd6bc]/82">{SITE_CONFIG.serviceNameEn}</span>
              <span className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#c4a289]/55">Private Reading Session</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 px-6 pb-12 sm:px-8 lg:px-12 lg:pb-16">
          <section className="mx-auto flex min-h-[calc(100vh-13rem)] w-full max-w-6xl items-center py-4 sm:py-8">
            <div className="grid w-full items-center gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(410px,0.98fr)] lg:gap-14 xl:gap-18">
              <div className="max-w-[44rem]">
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#f0c176]/75">한밤중에 조용히 문을 여는 타로집</p>
                <h1
                  className="mt-5 max-w-[8.8ch] font-serif text-[3.1rem] font-extrabold leading-[0.95] tracking-[-0.04em] text-[#fbf3e8] sm:text-[4.8rem] lg:text-[5.35rem] xl:text-[6.1rem]"
                >
                  <span className="block">김고양씨의</span>
                  <span className="block">타로집</span>
                </h1>
                <FixedLines
                  lines={heroDescriptionLines}
                  as="p"
                  className="mt-6 max-w-xl text-[15px] leading-8 text-[#ead6bd]/88 sm:text-base"
                  lineClassName="sm:whitespace-nowrap"
                />

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent('start_reading_clicked', { source: 'landing_hero' });
                      navigate('/spreads');
                    }}
                    className="inline-flex items-center justify-center gap-3 rounded-lg border border-[#bd3b0f]/55 bg-[#bd3b0f] px-6 py-4 text-sm font-semibold tracking-[0.18em] text-[#fff3df] transition-colors hover:border-[#d65a2a] hover:bg-[#9f3210]"
                  >
                    <UiIcon name="sparkles" className="h-4 w-4" />
                    리딩 시작
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/origins')}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#8a6a4b]/55 bg-[#18120f]/75 px-6 py-4 text-sm tracking-[0.16em] text-[#e8d1b7] transition-colors hover:bg-[#241914]"
                  >
                    <UiIcon name="book" className="h-4 w-4" />
                    묘안에 대해
                  </button>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-[38rem]">
                <div className="relative overflow-hidden border border-[#8a6a4b]/35 bg-[#211813] p-3 shadow-[0_34px_90px_-55px_rgba(0,0,0,0.86)] sm:p-4">
                  <div className="relative overflow-hidden bg-[#1d1110] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)]">
                    <div className="aspect-[2/3] w-full">
                      <img
                        src="/landing-main-image.png"
                        alt="김고양씨의 타로집 대표 이미지"
                        className="block h-full w-full object-cover object-center"
                      />
                    </div>
                    <img
                      src={`/cards/fronts/${heroCardIndexes[0]}.webp`}
                      alt="좌하단 오버레이 카드"
                      className="pointer-events-none absolute bottom-[-1.1rem] left-[-0.9rem] z-10 w-[31%] min-w-[8.5rem] -rotate-[8deg] border border-[#f0e0c9]/55 bg-[#f7f1e5] shadow-[0_26px_42px_rgba(0,0,0,0.36)] sm:bottom-[-1.5rem] sm:left-[-1.2rem] sm:w-[33%]"
                    />
                    <img
                      src={`/cards/fronts/${heroCardIndexes[1]}.webp`}
                      alt="우하단 오버레이 카드"
                      className="pointer-events-none absolute bottom-[-1.4rem] right-[-0.8rem] z-10 w-[36%] min-w-[9rem] rotate-[8deg] border border-[#f0e0c9]/55 bg-[#f7f1e5] shadow-[0_28px_46px_rgba(0,0,0,0.42)] sm:bottom-[-1.9rem] sm:right-[-1.3rem] sm:w-[38%]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
